import {
  AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChildren,
} from '@angular/core';
import { DateTime } from 'luxon';

import { LL } from 'i18n/i18n.service';
import { SettingsService } from 'services/settings.service';
import {
  breakableSetsText, OVERVIEW_BREAK_POINT, OverviewCell, OverviewCellKind, OverviewIntensityTag, OverviewMark, OverviewRow, OverviewSection, OverviewSessionTag,
  TRAINING_OVERVIEW_NO_VALUE, TRAINING_OVERVIEW_SESSIONS_STEP, TRAINING_OVERVIEW_WINDOW_DAYS, TrainingOverviewModel,
} from 'services/training-overview';
import { formatShortDate, Formatting } from 'util/formatting';
import { distanceFromRight, scrollLeftFor, spacerWidth } from './overview-table-parking';

/** a tap on an exercise: the row, and the DOM event, so the host can open a menu at the tapped element */
export interface TrainingOverviewRowTap {
  row: OverviewRow;
  event: Event;
}

/** one of the few cells a phone card shows, with the date of its session */
interface PhoneCell {
  dateIso: string;
  cell: OverviewCell;
  /** the cell's set text, one line per weight, for a cell whose sets used different weights; with break points */
  setLines: string[];
  /** the reps of a cell whose sets all used one weight; with break points */
  repsText: string;
}

/** a set text with the break points that let a phone row wrap it (#6691) */
function breakableSets(text: string): string {
  return breakableSetsText(text, Formatting.repSeparator, Formatting.times);
}

/** how many of a row's newest sessions a phone card shows */
const PHONE_CELL_COUNT = 4;

/**
 * The Training Overview (#5456, phase 3): per workout of the program, a row per exercise and a
 * column per logged session. A table on wide screens, a list of cards on phones.
 *
 * Presentational only. It draws a model that `TrainingOverviewDataService` built and emits taps;
 * it loads nothing and judges nothing. The marks and tags are decided in
 * `services/training-overview.ts`. See design-docs/coaching-dashboard.md, "Phase 3: the Training
 * Overview".
 */
@Component({
  selector: 'cs-training-overview',
  templateUrl: 'cs-training-overview.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTrainingOverviewComponent implements OnChanges, AfterViewChecked, OnDestroy {
  protected LL = LL;
  protected OverviewCellKind = OverviewCellKind;
  protected OverviewMark = OverviewMark;
  protected OverviewSessionTag = OverviewSessionTag;
  /** how many sessions "Show earlier" adds, for the button's text */
  protected readonly sessionsStep = TRAINING_OVERVIEW_SESSIONS_STEP;

  /** what to draw; undefined while the first load is running */
  @Input() model: TrainingOverviewModel | undefined;
  /** per section, whether older sessions can be shown; a missing entry counts as false */
  @Input() canShowEarlier: boolean[] = [];
  /** true while a load is running: a spinner before the first model, a fade over an old one */
  @Input() busy = false;
  /** true when the load failed; the grid is replaced by a message with a Retry button */
  @Input() failed = false;
  /** how many weeks of logs were loaded, for the "no logged workouts" sentence */
  @Input() loadedWeeks = TRAINING_OVERVIEW_WINDOW_DAYS / 7;

  @Output() retry = new EventEmitter<void>();
  @Output() rowTap = new EventEmitter<TrainingOverviewRowTap>();
  /** the index of the section whose "Show 10 earlier" was pressed */
  @Output() showEarlier = new EventEmitter<number>();

  /** each section's scrolling box, in section order (a section with no session has none) */
  @ViewChildren('scroller') private scrollers: QueryList<ElementRef<HTMLElement>>;

  /** the phone cards' cells, worked out once per model */
  private phoneCellsByRow = new Map<OverviewRow, PhoneCell[]>();
  /**
   * How far each table was scrolled from its RIGHT end before the model changed, by section
   * index. The newest session is on the right, so a fresh table opens at its right end (distance
   * 0), and after "Show 10 earlier" adds columns on the left the same columns stay in view.
   */
  private distanceFromRightBySection: number[] | undefined;
  /**
   * How far each table is scrolled from its right end now, by section index, kept up to date as the
   * person scrolls. A table can still change width after it was drawn (a font arriving, the box
   * settling); the resize watcher then puts it back to this distance, so the newest session does not
   * end up half outside the box.
   */
  private currentDistanceFromRight: number[] = [];
  /** notices a table or its box changing size after the table was placed */
  private resizeObserver?: ResizeObserver;
  /** the boxes whose scroll position is being remembered */
  private scrollingBoxes: HTMLElement[] = [];
  private isDestroyed = false;

  constructor(private settings: SettingsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['model']) return;
    this.phoneCellsByRow = new Map();
    for (const section of this.model?.sections ?? []) {
      for (const row of [...section.programRows, ...section.greyRows]) {
        this.phoneCellsByRow.set(row, this.newestCells(section, row));
      }
    }
    // remember where each table was scrolled, to put it back once the new columns are drawn
    const distances: number[] = [];
    for (const scroller of this.scrollers?.toArray() ?? []) {
      const element = scroller.nativeElement;
      const sectionIndex = Number(element.dataset['section']);
      distances[sectionIndex] = distanceFromRight(element.scrollWidth, element.clientWidth, element.scrollLeft);
    }
    this.distanceFromRightBySection = distances;
  }

  /**
   * Puts each table back where it was scrolled, once the new columns are drawn. `ngOnChanges`
   * recorded the distances before the model changed; a table drawn for the first time has none
   * and lands on its right end, where the newest session is.
   */
  ngAfterViewChecked(): void {
    const distances = this.distanceFromRightBySection;
    if (!distances) return;
    this.distanceFromRightBySection = undefined;
    this.currentDistanceFromRight = distances;
    this.stopWatchingTables();
    if (typeof ResizeObserver !== 'undefined') {
      // both the box and the table are watched: the box changes width with the window, the table when its
      // content settles. The re-fit waits for the next frame, because placing a table resizes its spacer
      // column, and doing that inside the observer's own callback makes the browser report a resize loop
      this.resizeObserver = new ResizeObserver(entries => {
        const boxes = new Set<HTMLElement | null>(entries.map(entry => {
          const target = entry.target as HTMLElement;
          return target.dataset['section'] !== undefined ? target : target.parentElement;
        }));
        requestAnimationFrame(() => {
          if (this.isDestroyed) return;
          boxes.forEach(box => this.placeTable(box));
        });
      });
    }
    for (const scroller of this.scrollers?.toArray() ?? []) {
      const box = scroller.nativeElement;
      this.placeTable(box);
      this.resizeObserver?.observe(box);
      const table = box.querySelector('table');
      if (table) this.resizeObserver?.observe(table);
      // a plain listener, not a template binding: a template listener would run change detection on the
      // whole grid on every scroll event, and remembering the position needs no redraw
      box.addEventListener('scroll', this.rememberScrollPosition, { passive: true });
      this.scrollingBoxes.push(box);
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.stopWatchingTables();
  }

  /** the person scrolled a table: remember where, measured from its right end */
  private readonly rememberScrollPosition = (event: Event): void => {
    const box = event.currentTarget as HTMLElement;
    this.currentDistanceFromRight[Number(box.dataset['section'])] = distanceFromRight(box.scrollWidth, box.clientWidth, box.scrollLeft);
  };

  /** stops watching the tables drawn for the previous model */
  private stopWatchingTables(): void {
    this.resizeObserver?.disconnect();
    for (const box of this.scrollingBoxes) {
      box.removeEventListener('scroll', this.rememberScrollPosition);
    }
    this.scrollingBoxes = [];
  }

  /**
   * Scrolls one table to the remembered distance from its right end (0 for a table drawn the first time).
   *
   * At the right end the shown sessions must be whole on both sides. The box rarely holds a whole number
   * of columns, so the empty spacer column at the end takes the leftover width: the leftmost session that
   * still fits then starts exactly at the edge of the fixed columns, and the newest ends inside the box.
   * Elsewhere the columns snap whole on their left edge (CSS scroll-snap). The arithmetic is in
   * `overview-table-parking.ts`; this measures the elements and writes the answers back.
   */
  private placeTable(element: HTMLElement | null): void {
    if (!element) return;
    const distance = this.currentDistanceFromRight[Number(element.dataset['section'])] ?? 0;
    const spacer = element.querySelector<HTMLElement>('th.col-spacer');
    const sessionHeads = Array.from(element.querySelectorAll<HTMLElement>('th.col-session'));
    if (spacer && sessionHeads.length > 0) {
      // measure with the spacer collapsed, so its old width is not counted as content
      spacer.style.minWidth = '0px';
      const width = spacerWidth({
        boxWidth: element.clientWidth,
        // the fixed columns' width is the scroll padding the stylesheet gives the box
        fixedWidth: parseFloat(getComputedStyle(element).scrollPaddingLeft) || 0,
        contentEnd: spacer.offsetLeft,
        sessionStarts: sessionHeads.map(head => head.offsetLeft),
      });
      spacer.style.minWidth = `${width}px`;
    }
    element.scrollLeft = scrollLeftFor(element.scrollWidth, element.clientWidth, distance);
  }

  /** true when no section has a session at all */
  protected get hasNoSessions(): boolean {
    return !!this.model && this.model.sections.every(section => section.sessionCount === 0);
  }

  /** a column's date in the user's date format */
  protected formatDate(dateIso: string): string {
    return formatShortDate(DateTime.fromISO(dateIso), this.settings);
  }

  protected phoneCells(row: OverviewRow): PhoneCell[] {
    return this.phoneCellsByRow.get(row) ?? [];
  }

  /** "Sets 3 · Target 6/10" for a phone card; the target is left out when the row has none */
  protected phoneTargetsText(row: OverviewRow): string {
    if (!row.isInProgram || !row.setsText || row.setsText === TRAINING_OVERVIEW_NO_VALUE) return '';
    return row.repTargetText && row.repTargetText !== TRAINING_OVERVIEW_NO_VALUE
      ? LL.trainingOverview.setsAndTarget({ sets: row.setsText, target: row.repTargetText })
      : LL.trainingOverview.setsOnly({ sets: row.setsText });
  }

  /** a row can be tapped unless it is a grey row whose exercise no longer exists: nothing could open for it */
  protected isTappable(row: OverviewRow): boolean {
    return row.isInProgram || !row.isExerciseMissing;
  }

  protected onRowTap(row: OverviewRow, event: Event): void {
    if (!this.isTappable(row)) return;
    this.rowTap.emit({ row, event });
  }

  protected markText(cell: OverviewCell): string {
    switch (cell.mark) {
      case OverviewMark.Same: return LL.trainingOverview.markSame();
      case OverviewMark.Down: return LL.trainingOverview.markDown();
      default: return '';
    }
  }

  protected intensityText(cell: OverviewCell): string {
    switch (cell.intensityTag) {
      case OverviewIntensityTag.Heavy: return LL.trainingOverview.tags.heavy();
      case OverviewIntensityTag.Medium: return LL.trainingOverview.tags.medium();
      case OverviewIntensityTag.Light: return LL.trainingOverview.tags.light();
      default: return '';
    }
  }

  protected sessionTagText(cell: OverviewCell): string {
    switch (cell.sessionTag) {
      case OverviewSessionTag.Test: return LL.trainingOverview.tags.test();
      case OverviewSessionTag.Deload: return LL.trainingOverview.tags.deload();
      case OverviewSessionTag.Break: return LL.trainingOverview.tags.break();
      case OverviewSessionTag.Cap: return LL.trainingOverview.tags.cap();
      default: return '';
    }
  }

  /** the newest few cells of a row that hold something, newest first, each with its session's date */
  private newestCells(section: OverviewSection, row: OverviewRow): PhoneCell[] {
    const cells: PhoneCell[] = [];
    for (let i = section.columns.length - 1; i >= 0 && cells.length < PHONE_CELL_COUNT; i--) {
      const cell = row.cells[i];
      if (cell && cell.kind !== OverviewCellKind.Empty) {
        // the formatter writes its own separator between the sets of one weight and the next
        cells.push({
          dateIso: section.columns[i].dateIso,
          cell,
          setLines: cell.setsText.split(Formatting.setGroupSeparator).map(breakableSets),
          // the times sign is drawn on its own before these reps, so the break point after it leads them
          repsText: OVERVIEW_BREAK_POINT + breakableSets(cell.repsText),
        });
      }
    }
    return cells;
  }
}
